import Vue from 'vue'
import axios from 'axios'
import {
  baseURL,
  contentType,
  method,
  invalidCode,
  noPermissionCode,
  requestTimeout,
  successCode,
  tokenName,
  fileContentType,
} from '@/config'
import store from '@/store'
import qs from 'qs'
import router from '@/router'
import { isArray } from '@/utils/validate'
import dayjs from 'dayjs'

// ========== 刷新队列支持 ==========
let isRefreshing = false // 刷新锁
const refreshSubscribers = [] // 等待队列

function subscribeTokenRefresh(callback) {
  refreshSubscribers.push(callback)
}

function onRrefreshed() {
  isRefreshing = false
  refreshSubscribers.forEach(callback => callback())
  refreshSubscribers.length = 0
}
// ===================================

const instance = axios.create({
  baseURL,
  method: method,
  timeout: requestTimeout,
  headers: {
    'Content-Type': contentType,
  },
})

instance.interceptors.request.use(
  config => {
    if (config.header) {
      Object.keys(config.header).forEach(item => {
        config.headers[item] = config.header[item]
      })
    }
    if (store.getters['user/accessToken'] && !config.flag) {
      config.headers[tokenName] = 'Bearer ' + store.getters['user/accessToken']
    }
    if (config.data && config.headers['Content-Type'] === 'application/x-www-form-urlencoded;charset=UTF-8') {
      config.data = qs.stringify(config.data)
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

instance.interceptors.response.use(
  response => {
    const { data, config } = response
    if (response.headers && 'content-type' in response.headers && fileContentType.includes(response.headers['content-type'])) {
      let fileName = ''
      if (response.headers && 'content-disposition' in response.headers) {
        const dispositions = response.headers['content-disposition'].split('=')
        fileName = dispositions.length > 1 ? dispositions[1].slice(0, dispositions[1].lastIndexOf(';')) : ''
      }
      return {
        code: 200,
        msg: '',
        data: { FileName: decodeURI(fileName), Blob: data },
      }
    }
    const { code, msg } = data
    const codeVerificationArray = isArray(successCode) ? [...successCode] : [...[successCode]]
    if (codeVerificationArray.includes(code)) {
      return data
    }
    handleCode(code, msg)
    return Promise.reject(`请求执行失败：${JSON.stringify({ url: config.url, code, msg })}`)
  },
  async error => {
    const { response, message } = error
    const originalRequest = error.config

    // ========== 401 自动刷新逻辑（增强版）==========
    if (response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (response?.data?.code == 401) {
        const loginErr = response.data.msg ? response.data.msg : '登录已失效，请重新登录'
        Vue.prototype.$baseMessage(loginErr, 'error')
        await doLogoutAndRedirect()
        return Promise.reject(loginErr)
      }
      if (isRefreshing) {
        // 不再直接登出，而是排队等待
        return new Promise(resolve => {
          subscribeTokenRefresh(() => resolve(instance(originalRequest)))
        })
      }
      isRefreshing = true
      originalRequest._retry = true
      try {
        const success = await store.dispatch('user/refreshAccessToken')
        if (success) {
          onRrefreshed() // 通知所有排队请求重试
          return instance(originalRequest)
        } else {
          await doLogoutAndRedirect()
          return Promise.reject('请求过期')
        }
      } catch (err) {
        await doLogoutAndRedirect()
        return Promise.reject(err)
      } finally {
        isRefreshing = false
      }
    }
    if (response && response.data) {
      const { status, data } = response
      handleCode(status, data.msg || message)
      return Promise.reject(error)
    } else {
      let { message } = error
      if (message === 'Network Error') {
        message = '请求繁忙，请稍后重试'
      }
      if (message.includes('timeout')) {
        message = '请求超时'
      }
      if (message.includes('Request failed with status code')) {
        const code = message.substr(message.length - 3)
        message = `请求${code}异常`
      }
      Vue.prototype.$baseMessage(message || '请求未知异常', 'error')
      return Promise.reject(error)
    }
  }
)

async function doLogoutAndRedirect() {
  await store.dispatch('tabsBar/delAllRoutes')
  await store.dispatch('user/logout')
  router.push({ path: '/login' }).catch(() => {})
}

const handleCode = (code, msg) => {
  switch (code) {
    case invalidCode:
      Vue.prototype.$baseMessage(msg || `接口${code}异常`, 'error')
      store.dispatch('user/resetAccessToken').catch(() => {})
      location.reload()
      break
    case noPermissionCode:
      router.push({ path: '/401' }).catch(() => {})
      break
    default:
      Vue.prototype.$baseMessage(msg || `接口${code}异常`, 'error')
      break
  }
}

export default instance
